import { unref } from 'vue';
import { UPC_VALIDATION_MESSAGES } from '../consts/validation';
import type { Orientation, Color, Size } from '~/declaration/traits';

type VaidateUPCParams = {
  selectedColor: Color | null;
  selectedSize: Size | null;
  selectedOrientation: Orientation | undefined;
  colors?: Color[];
  sizes?: Size[];
  orientations?: Orientation[];
  isColorAvailable: (color: Color, selectedSizeId: number, typeId?: number) => boolean;
  isSizeAvailable: (size: Size, selectedColorId?: number, typeId?: number) => boolean;
  isConsolidatedMaster?: boolean;
  typeName?: string;
  sizeName?: string;
  excludeColorLabel?: boolean;
  isBcomConsolidatedDropdownCopyUpdate?: boolean;
  isAnyFulfillmentAvailable?: boolean;
};

/**
 * * Validate the selected color and size
 *
 * TODO - add product type validation?
 * TODO - some of products have no size or not type
 * ? - colors, sizes and types are traits of a product with similar structure
 * ? - it seems like there should be a common way to validate them
 */
export const validateUPC = ({
  selectedColor,
  selectedSize,
  selectedOrientation,
  colors,
  sizes,
  orientations,
  isColorAvailable,
  isSizeAvailable,
  isConsolidatedMaster = false,
  typeName,
  sizeName,
  excludeColorLabel,
  isBcomConsolidatedDropdownCopyUpdate,
  isAnyFulfillmentAvailable,
}: VaidateUPCParams): string | null => {
  if (isAnyFulfillmentAvailable) {
    return null;
  }
  const colorsExist = colors && colors.length;
  const sizesExist = sizes && sizes.length;
  const orientationsExist = orientations && orientations.length;

  const typeLabel = (typeName || 'orientation').toLowerCase();
  const sizeLabel = (sizeName || 'size').toLowerCase();

  if (colorsExist && !selectedColor) {
    return UPC_VALIDATION_MESSAGES.COLOR_NOT_SELECTED;
  }
  if (orientationsExist && !selectedOrientation) {
    if (sizesExist && !selectedSize) {
      return `${UPC_VALIDATION_MESSAGES.TRAIT_NOT_SELECTED} ${sizeLabel} and ${typeLabel}`;
    }
    if (!typeName) {
      return UPC_VALIDATION_MESSAGES.TYPE_NOT_SELECTED;
    }
    return `${UPC_VALIDATION_MESSAGES.TRAIT_NOT_SELECTED} ${typeName.toLowerCase()}`;
  }
  if (sizesExist && !selectedSize && isConsolidatedMaster) {
    return UPC_VALIDATION_MESSAGES.VALUE_NOT_SELECTED;
  }
  if (sizesExist && !selectedSize) {
    if (!sizeName) {
      if (isBcomConsolidatedDropdownCopyUpdate) {
        return UPC_VALIDATION_MESSAGES.SELECTION_NOT_SELECTED;
      }
      return UPC_VALIDATION_MESSAGES.SIZE_NOT_SELECTED;
    }
    return `${UPC_VALIDATION_MESSAGES.TRAIT_NOT_SELECTED} ${sizeName.toLowerCase()}`;
  }

  if (
    (colorsExist && !unref(isColorAvailable)(selectedColor, selectedSize?.id, selectedOrientation?.id)) ||
    (sizesExist && !unref(isSizeAvailable)(selectedSize, selectedColor?.id, selectedOrientation?.id))
  ) {
    if (isConsolidatedMaster) {
      return UPC_VALIDATION_MESSAGES.VALUE_COLOR_NOT_AVAILABLE;
    }
    if (selectedSize && selectedOrientation) {
      const label = `${sizeLabel}/${typeLabel} ${UPC_VALIDATION_MESSAGES.COMBINATION_NOT_AVAILABLE}`;
      if (excludeColorLabel) {
        return `This ${label}`;
      }
      return `This color/${label}`;
    }
    return UPC_VALIDATION_MESSAGES.SIZE_COLOR_NOT_AVAILABLE;
  }
  return null;
};

export default {
  validateUPC,
};
